import { createContext, ReactNode } from 'react';
import {
  Dialog,
  DialogProps,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogContentText as MuiDialogContentText,
  DialogActions as MuiDialogActions,
  Button,
} from '@mui/material';

type AlertVariant = 'default' | 'alert';

const AlertVariantContext = createContext<AlertVariant>('default');

interface AlertDialogProps {
  variant?: AlertVariant;
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  actions: {
    cancel: {
      label: ReactNode;
      onClick: () => void;
    };
    confirm: {
      label: ReactNode;
      onClick: () => void;
      disabled?: boolean;
      /** Request in flight: the button keeps focus but ignores clicks, and the dialog can't be dismissed. */
      loading?: boolean;
      /** Label shown (and announced) while loading. */
      loadingLabel?: ReactNode;
    };
  };
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;
  sx?: DialogProps['sx'];
}

export const AlertDialog = ({
  variant = 'alert',
  open,
  onClose,
  title,
  description,
  children,
  actions,
  maxWidth = 'sm',
  fullWidth,
  sx,
}: AlertDialogProps) => {
  const loading = !!actions.confirm.loading;
  const loadingLabel = actions.confirm.loadingLabel ?? actions.confirm.label;
  const handleClose = () => {
    if (!loading) onClose();
  };
  const handleConfirm = () => {
    if (!loading) actions.confirm.onClick();
  };

  return (
    <AlertVariantContext.Provider value={variant}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={maxWidth}
        fullWidth={fullWidth}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 16 },
            padding: 24,
            width: { xs: '100%', sm: 'auto' },
            maxWidth: {
              xs: '100%',
              sm: '600px',
              md: '700px',
            },
            maxHeight: { xs: '100vh', sm: '90vh' },
            borderRadius: { xs: 0, sm: 1 },
          },
          '& .MuiDialog-container': {
            alignItems: 'center',
          },
          ...sx,
        }}
      >
        <MuiDialogTitle
          sx={{
            fontSize: 18,
            padding: 0,
            mb: 12,
          }}
        >
          {title}
        </MuiDialogTitle>
        <MuiDialogContent sx={{ padding: 0 }}>
          {description && (
            <MuiDialogContentText sx={{ fontSize: 13 }}>
              {description}
            </MuiDialogContentText>
          )}
          {children}
          <span role="status" className="sr-only">
            {loading ? loadingLabel : ''}
          </span>
        </MuiDialogContent>
        <MuiDialogActions
          sx={{
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            padding: 0,
            gap: 12,
            marginTop: 24,
            '& > *:not(:first-of-type)': {
              marginLeft: 0,
            },
          }}
        >
          <Button
            variant="default"
            onClick={actions.cancel.onClick}
            disabled={loading}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              margin: 0,
            }}
          >
            {actions.cancel.label}
          </Button>
          <Button
            variant={variant === 'alert' ? 'warning' : 'primary'}
            onClick={handleConfirm}
            disabled={actions.confirm.disabled}
            // aria-disabled rather than disabled so focus stays on the button while loading
            aria-disabled={loading || undefined}
            aria-busy={loading || undefined}
            autoFocus
            sx={{
              width: { xs: '100%', sm: 'auto' },
              margin: 0,
            }}
          >
            {loading ? loadingLabel : actions.confirm.label}
          </Button>
        </MuiDialogActions>
      </Dialog>
    </AlertVariantContext.Provider>
  );
};
